// Port of src/app/services/spotify/spotify-user-account.service.ts.
// OAuth PKCE redirect flow — storage keys and redirect parameters identical to
// the Angular app.

import { http } from '../http';
import { environment } from '../../config/environment';
import * as spotifyTokens from './spotify-tokens';

export async function authenticateUser(): Promise<void> {
  // Clear previous user data
  spotifyTokens.clearCredentials();

  const codeChallenge = await generateCodeChallengeAndStoreVerifier();
  const authEndpoint = environment.spotifyAuthEndpoint;
  const scopes = 'user-read-private playlist-modify-public playlist-read-collaborative playlist-read-private playlist-modify-private';
  window.location.href = `${authEndpoint}?client_id=${encodeURIComponent(environment.spotifyAppClientId)}&response_type=code&redirect_uri=${encodeURIComponent(environment.spotifyRedirectUri)}&scope=${encodeURIComponent(scopes)}&code_challenge_method=S256&code_challenge=${codeChallenge}&show_dialog=true`;
}

export async function handleAuthenticationCallback(code: string): Promise<any> {
  const tokenEndpoint = environment.spotifyTokenEndpoint;
  const codeVerifier = spotifyTokens.getCodeVerifierAddress();

  const body = new URLSearchParams();
  body.set('client_id', environment.spotifyAppClientId);
  body.set('grant_type', 'authorization_code');
  body.set('code', code);
  body.set('redirect_uri', environment.spotifyRedirectUri);
  body.set('code_verifier', codeVerifier || '');

  const res = await http.post<any>(tokenEndpoint, body.toString(), {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
  });
  return res.data;
}

export function saveUserData(accessToken: string, refreshToken: string, email: string): Promise<any> {
  spotifyTokens.setTokens(accessToken, refreshToken, email);
  setIsSpotifyConnected();
  return getUserProfile();
}

export function setUserId(clientId: string): void {
  spotifyTokens.setUserId(clientId);
}

export function setIsSpotifyConnected(): void {
  spotifyTokens.setIsSpotifyConnected();
}

export function getConnectedUserEmailAddress(): string {
  return spotifyTokens.getConnectedUserEmailAddress();
}

export function disconnectAccount(): void {
  spotifyTokens.clearCredentials();
}

async function getUserProfile(): Promise<any> {
  const accessToken = spotifyTokens.getAccessToken();
  if (!accessToken) {
    throw new Error('No access token available');
  }

  const res = await http.get<any>('https://api.spotify.com/v1/me', {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });
  return res.data;
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Port of generateCodeChallengeObservable (Observable → Promise): generates a
// verifier, persists it, resolves with the matching challenge.
async function generateCodeChallengeAndStoreVerifier(): Promise<string> {
  const codeVerifier = generateCodeVerifier();
  spotifyTokens.setCodeVerifier(codeVerifier);
  return generateCodeChallenge(codeVerifier);
}
